import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../config/axios';

interface PickupLocation {
  id: string;
  name: string;
  address: string;
  is_active: boolean;
  created_at: string;
}

interface LocationInput {
  name: string;
  address: string;
  is_active: boolean;
}

const PickupLocationManagementPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState<PickupLocation | null>(null);
  const [formData, setFormData] = useState<LocationInput>({
    name: '',
    address: '',
    is_active: true,
  });
  const [error, setError] = useState<string>('');

  // Fetch all locations
  const { data: locations, isLoading } = useQuery<PickupLocation[]>({
    queryKey: ['adminPickupLocations'],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/admin/pickup-locations');
      return response.data;
    },
  });

  // Create location mutation
  const createMutation = useMutation({
    mutationFn: async (data: LocationInput) => {
      const response = await axiosInstance.post('/api/admin/pickup-locations', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminPickupLocations'] });
      resetForm();
      setError('');
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to create location');
    },
  });

  // Update location mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<LocationInput> }) => {
      const response = await axiosInstance.put(`/api/admin/pickup-locations/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminPickupLocations'] });
      resetForm();
      setError('');
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to update location');
    },
  });

  // Delete location mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await axiosInstance.delete(`/api/admin/pickup-locations/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminPickupLocations'] });
    },
    onError: (err: any) => {
      alert(err.response?.data?.detail || 'Failed to delete location');
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      is_active: true,
    });
    setEditingLocation(null);
    setShowForm(false);
  };

  const handleEdit = (location: PickupLocation) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      address: location.address,
      is_active: location.is_active,
    });
    setShowForm(true);
    setError('');
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this location? This can only be done if there are no pickup dates.')) {
      deleteMutation.mutate(id);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Location name is required');
      return;
    }

    if (!formData.address.trim()) {
      setError('Location address is required');
      return;
    }

    if (editingLocation) {
      updateMutation.mutate({ id: editingLocation.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>Pickup Location Management</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              + Add Location
            </button>
          )}
          <a
            href="/admin"
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '4px',
              fontWeight: 'bold',
            }}
          >
            ← Back to Dashboard
          </a>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <section style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '2px solid #28a745' }}>
          <h2>{editingLocation ? 'Edit Location' : 'Add New Location'}</h2>
          
          {error && (
            <div style={{ padding: '10px', marginBottom: '15px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px', border: '1px solid #f5c6cb' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Location Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="e.g., Downtown Market"
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Address *
              </label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                required
                placeholder="Full address including street, city, postal code"
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontFamily: 'inherit',
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  style={{ marginRight: '8px' }}
                />
                <span style={{ fontWeight: 'bold' }}>Active</span>
              </label>
              <p style={{ margin: '5px 0 0 24px', fontSize: '12px', color: '#666' }}>
                Inactive locations won't be shown to customers
              </p>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                style={{
                  padding: '10px 30px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                {createMutation.isPending || updateMutation.isPending ? 'Saving...' : (editingLocation ? 'Update' : 'Create')}
              </button>
              <button
                type="button"
                onClick={resetForm}
                style={{
                  padding: '10px 30px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </section>
      )}

      {/* Locations List */}
      <section>
        <h2>Pickup Locations</h2>
        
        {locations && locations.length > 0 ? (
          <div style={{ display: 'grid', gap: '15px' }}>
            {locations.map(location => (
              <div
                key={location.id}
                style={{
                  padding: '20px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: location.is_active ? 'white' : '#f8f9fa',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: '0 0 10px 0' }}>
                      {location.name}
                      {!location.is_active && (
                        <span style={{ marginLeft: '10px', padding: '4px 8px', backgroundColor: '#dc3545', color: 'white', fontSize: '12px', borderRadius: '4px' }}>
                          Inactive
                        </span>
                      )}
                    </h3>
                    <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                      {location.address}
                    </p>
                    <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#999' }}>
                      Created: {new Date(location.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => handleEdit(location)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(location.id)}
                      disabled={deleteMutation.isPending}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <p style={{ fontSize: '18px', color: '#666', marginBottom: '20px' }}>
              No pickup locations created yet.
            </p>
            <button
              onClick={() => setShowForm(true)}
              style={{
                padding: '12px 30px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              Add First Location
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default PickupLocationManagementPage;
